/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import {
  Button,
  CardBody,
  Typography,
  SkeletonBlock
} from '@one-thd/sui-atomic-components';
import EditStoreLocation from './EditStoreLocation';

import { sortStores, fetchStoresData, trackAnalytics } from './helpers';
import { setStore, setRegionalSelections } from '../../actions/store';
import { updatePath } from '../../actions/pathActions';
import { savePricingDetailsFromThd } from '../../actions';
import { getRegionalSelections } from '../../api/getRegionalSelections';

const ProjectLocation = ({ isSkeleton }) => {
  if (isSkeleton) {
    return (
      <CardBody>
        <div className="storelocation-text-container">
          <Typography lineclamp="1" style={{ marginRight: '2rem' }}>
            <span style={{ fontWeight: 'bold' }}>
              Installation ZIP Code:&nbsp;
              <span className="sui-w-14 sui-inline-block"><SkeletonBlock height={3} width={12} /></span>
            </span>
          </Typography>
          <Typography lineclamp="1">
            <span style={{ fontWeight: 'bold' }}>
              Your Store:&nbsp;
              <span className="sui-w-14 sui-inline-block"><SkeletonBlock height={3} width={12} /></span>
            </span>
          </Typography>
        </div>
        <Button variant="text" style={{ border: 'none' }}>
          <Typography paragraph decoration="underline">Edit Location</Typography>
        </Button>
      </CardBody>
    );

  }

  const dispatch = useDispatch();
  const [showEditLocation, setShowEditLocation] = useState(false);
  const [yourStoreLocation, setYourStoreLocation] = useState('');
  const { storeInfo, storeNumber, zipcode } = useSelector((state) => state.store);
  const { productId } = useSelector((state) => state.config);
  const [storeApiInfo, setStoreApiInfo] = useState({});
  const [localZipCode, setLocalZipCode] = useState(zipcode);

  const updateStore = (stores = storeApiInfo.stores, storeNum, callLocation) => {
    trackAnalytics('set store', callLocation);
    const { sortedStores, store: newStore } = sortStores(stores, storeNum);
    setStoreApiInfo({ ...storeApiInfo, stores: sortedStores, store: newStore });
    dispatch(setStore(newStore, storeNumber));
    dispatch(updatePath());
    dispatch(savePricingDetailsFromThd({}));
  };

  useEffect(() => {
    async function fetchStores() {
      const data = await fetchStoresData(zipcode, storeNumber);
      if (data.store) {
        updateStore(data.stores, data.store.storeId);
      }
      setStoreApiInfo(data);
    }
    fetchStores();
  }, []);

  useEffect(() => {
    const fetchRegionalSelections = async () => {
      if (!isEmpty(storeInfo)) {
        const { name, storeId, address: {
          street, city, state, postalCode, zipcode: zip
        } } = storeInfo;
        const storeLoc = `${name} #${storeId} ${street} ${city}, ${state} ${postalCode || zip}`;
        setYourStoreLocation(storeLoc);
        const regionalSelections = await getRegionalSelections(productId, storeId, postalCode || zip);
        if (regionalSelections) {
          dispatch(setRegionalSelections(regionalSelections));
        }
      }
    };
    fetchRegionalSelections();
  }, [storeInfo]);

  const fetchStores = async (zip, storeNum = storeNumber) => {
    const data = await fetchStoresData(zip, storeNum);
    setStoreApiInfo(data);
  };

  return showEditLocation
    ? (
      <EditStoreLocation
        fetchStores={fetchStores}
        localZipCode={localZipCode}
        setLocalZipCode={setLocalZipCode}
        setShowEditStoreLocation={setShowEditLocation}
        storeNumber={storeNumber}
        storeApiInfo={storeApiInfo}
        trackAnalytics={trackAnalytics}
        updateStore={updateStore}
        zipcode={zipcode}
      />
    )
    : (
      <CardBody>
        <div className="storelocation-text-container">
          <Typography lineclamp="1" style={{ marginRight: '2rem' }}>
            <span style={{ fontWeight: 'bold' }}>Installation ZIP Code:&nbsp;</span>
            <span className="sui-w-14 sui-inline-block">{localZipCode.length === 5 ? localZipCode : zipcode}</span>
          </Typography>
          <Typography lineclamp="1">
            <span style={{ fontWeight: 'bold' }}>Your Store:&nbsp;</span>
            {yourStoreLocation}
          </Typography>
        </div>
        <Button variant="text" style={{ border: 'none' }} onClick={() => setShowEditLocation(true)}>
          <Typography paragraph decoration="underline">Edit Location</Typography>
        </Button>
      </CardBody>
    );
};

ProjectLocation.propTypes = {
  isSkeleton: PropTypes.bool
};

ProjectLocation.defaultProps = {
  isSkeleton: false
};

export default ProjectLocation;
